import type { CashbackLedgerEntry, CashbackTxnType } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Button, Card, DataTable, EmptyState, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { useAuthStore } from '../../lib/authStore.js';
import { listCashbackLedger } from '../../lib/cashback.js';
import { formatDate } from '../../lib/intl.js';
import { formatDecimals } from '../../lib/money.js';

const TONE: Record<string, 'success' | 'danger' | 'info' | 'neutral' | 'warning'> = {
  earn: 'success', redeem: 'info', adjust: 'warning', expire: 'neutral', reverse: 'danger',
};

export function CashbackLedgerPage(): JSX.Element {
  const { t, i18n } = useTranslation('cashback');
  const { t: tc } = useTranslation();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  // Cashback is a base-currency wallet — decimals follow that currency's exponent.
  const fmtSigned = (n: string): string => `${Number(n) > 0 ? '+' : ''}${formatDecimals(n, baseCurrency, i18n.language)}`;
  const [rows, setRows] = useState<CashbackLedgerEntry[] | null>(null);
  // Filtering happens SERVER-side rather than by hiding rows already fetched:
  // the list is capped, so a client-side date filter would quietly search only
  // the newest page and show a total that isn't one.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [type, setType] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        setRows(await listCashbackLedger({
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(type ? { type: type as CashbackTxnType } : {}),
          limit: 1000,
        }));
      } catch { toast.error(tc('errors:server')); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, type]);

  if (!rows) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }

  const columns = [
    {
      id: 'date', header: t('ledger.columns.date', { defaultValue: 'Date' }),
      cell: (r: CashbackLedgerEntry) => <span className="whitespace-nowrap text-fg-muted">{formatDate(r.createdAt, { locale: i18n.language })}</span>,
      sortValue: (r: CashbackLedgerEntry) => r.createdAt,
    },
    {
      id: 'customer', header: t('ledger.columns.customer', { defaultValue: 'Customer' }),
      cell: (r: CashbackLedgerEntry) => (
        <div className="min-w-0">
          <div className="truncate">{r.partyName}</div>
          {r.partyCode ? <div className="font-mono text-xs text-fg-muted" dir="ltr">{r.partyCode}</div> : null}
        </div>
      ),
      // Searchable by code too — staff look a customer up by the code on their card.
      searchValue: (r: CashbackLedgerEntry) => `${r.partyName} ${r.partyCode ?? ''}`,
    },
    {
      id: 'type', header: t('ledger.columns.type', { defaultValue: 'Type' }),
      cell: (r: CashbackLedgerEntry) => <Badge tone={TONE[r.type] ?? 'neutral'}>{t(`type.${r.type}`, { defaultValue: r.type })}</Badge>,
      searchValue: (r: CashbackLedgerEntry) => r.type,
    },
    {
      id: 'amount', header: t('ledger.columns.amount', { defaultValue: 'Amount' }), align: 'end' as const,
      cell: (r: CashbackLedgerEntry) => (
        <span className={`font-mono font-semibold ${Number(r.amount) >= 0 ? 'text-success' : 'text-danger'}`}>{fmtSigned(r.amount)}</span>
      ),
      sortValue: (r: CashbackLedgerEntry) => Number(r.amount),
    },
    {
      id: 'balance', header: t('ledger.columns.balance', { defaultValue: 'Balance' }), align: 'end' as const,
      cell: (r: CashbackLedgerEntry) => <span className="font-mono text-fg-muted">{formatDecimals(r.balanceAfter, baseCurrency, i18n.language)}</span>,
    },
    {
      id: 'document', header: t('ledger.columns.document', { defaultValue: 'Document' }),
      // The number a shop says out loud, with the kind of document underneath.
      // "sales_order" and a uuid told nobody which sale this cashback came from.
      cell: (r: CashbackLedgerEntry) => (
        <div className="min-w-0">
          <div className="font-mono text-xs" dir="ltr">{r.sourceNumber ?? '—'}</div>
          {r.sourceType ? (
            <div className="text-xs text-fg-muted">{t(`source.${r.sourceType}`, { defaultValue: r.sourceType })}</div>
          ) : null}
        </div>
      ),
      searchValue: (r: CashbackLedgerEntry) => `${r.sourceNumber ?? ''} ${r.sourceType ?? ''}`,
      sortValue: (r: CashbackLedgerEntry) => r.sourceNumber ?? '',
    },
    {
      id: 'note', header: t('ledger.columns.note', { defaultValue: 'Note' }),
      cell: (r: CashbackLedgerEntry) => <span className="text-xs text-fg-muted">{r.note ?? '—'}</span>,
      searchValue: (r: CashbackLedgerEntry) => r.note ?? '',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('ledger.title', { defaultValue: 'Cashback ledger' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('ledger.subtitle', { defaultValue: 'Every cashback movement, with the document that caused it.' })}
        </p>
      </header>

      <Card className="flex flex-wrap items-end gap-3">
        <FormField id="cb-from" label={t('ledger.from', { defaultValue: 'From' })}>
          <Input id="cb-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to || undefined} />
        </FormField>
        <FormField id="cb-to" label={t('ledger.to', { defaultValue: 'To' })}>
          <Input id="cb-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from || undefined} />
        </FormField>
        <FormField id="cb-type" label={t('ledger.columns.type', { defaultValue: 'Type' })}>
          <select
            id="cb-type"
            value={type}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setType(e.target.value)}
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
          >
            <option value="">{t('ledger.allTypes', { defaultValue: 'All types' })}</option>
            {(['earn', 'redeem', 'adjust', 'expire', 'reverse'] as const).map((k) => (
              <option key={k} value={k}>{t(`type.${k}`, { defaultValue: k })}</option>
            ))}
          </select>
        </FormField>
        {(from || to || type) ? (
          <Button variant="ghost" onClick={() => { setFrom(''); setTo(''); setType(''); }}>
            {t('ledger.clear', { defaultValue: 'Clear' })}
          </Button>
        ) : null}
        {/* Totals for exactly what is on screen, so a filtered range answers
            "how much did we give away in June?" without exporting anything. */}
        <div className="ms-auto flex gap-5 text-sm">
          <Total
            label={t('ledger.totalEarned', { defaultValue: 'Earned' })}
            value={sumWhere(rows, (n) => n > 0)}
            tone="text-success"
            currency={baseCurrency}
            locale={i18n.language}
          />
          <Total
            label={t('ledger.totalSpent', { defaultValue: 'Spent' })}
            value={sumWhere(rows, (n) => n < 0)}
            tone="text-danger"
            currency={baseCurrency}
            locale={i18n.language}
          />
          <div>
            <div className="text-xs text-fg-muted">{t('ledger.rows', { defaultValue: 'Rows' })}</div>
            <div className="font-mono font-semibold">{rows.length}</div>
          </div>
        </div>
      </Card>
      {rows.length === 0 ? (
        <EmptyState title={t('ledger.empty', { defaultValue: 'No cashback activity yet.' })} />
      ) : (
        <Card className="p-0">
          <DataTable columns={columns} data={rows} rowKey={(r) => r.id} searchPlaceholder={t('ledger.search', { defaultValue: 'Search…' })} />
        </Card>
      )}
    </div>
  );
}

/** Sum the rows whose signed amount matches — earned vs spent, on screen only. */
function sumWhere(rows: CashbackLedgerEntry[], keep: (n: number) => boolean): string {
  return String(rows.reduce((n, r) => (keep(Number(r.amount)) ? n + Number(r.amount) : n), 0));
}

function Total({
  label, value, tone, currency, locale,
}: { label: string; value: string; tone: string; currency: string; locale: string }): JSX.Element {
  return (
    <div>
      <div className="text-xs text-fg-muted">{label}</div>
      <div className={`font-mono font-semibold ${tone}`}>{formatDecimals(value, currency, locale)}</div>
    </div>
  );
}
